import { useState, useCallback, useMemo, useEffect } from 'react';
import { Plus, X, CheckCircle2, AlertCircle, Eye, Pencil } from 'lucide-react';
import { useCreateTaskMutation } from '@/store/api/apiSlice';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { renderMarkdown } from '@/lib/markdown';
import type { Task, TaskStatus, CreateTaskDto } from '@/types/index';

// ─── Types ──────────────────────────────────────────────────────────────────

interface CreateTaskModalProps {
  open: boolean;
  onClose: () => void;
  defaultParentId?: string;
  defaultOwnerId?: string;
  allTasks: Task[];
}

type FeedbackState =
  | { kind: 'idle' }
  | { kind: 'success' }
  | { kind: 'error'; message: string };

// ─── Status config ──────────────────────────────────────────────────────────

const statusOptions: { value: TaskStatus; label: string; dotClass: string }[] = [
  {
    value: 'pending',
    label: 'Pending',
    dotClass: 'text-gray-400 dark:text-gray-500',
  },
  {
    value: 'in_progress',
    label: 'In Progress',
    dotClass: 'text-blue-500 dark:text-blue-400',
  },
  {
    value: 'blocked',
    label: 'Blocked',
    dotClass: 'text-red-500 dark:text-red-400',
  },
  {
    value: 'completed',
    label: 'Completed',
    dotClass: 'text-green-500 dark:text-green-400',
  },
  {
    value: 'cancelled',
    label: 'Cancelled',
    dotClass: 'text-gray-400 dark:text-gray-600',
  },
];

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Build a breadcrumb path string from a task's path field. */
function buildBreadcrumb(task: Task, allTasks: Task[]): string {
  if (!task.path) return task.title;
  const ids = task.path.split('/').filter(Boolean);
  const parts: string[] = [];
  for (const id of ids) {
    const found = allTasks.find((t) => t._id === id);
    if (found) parts.push(found.title);
  }
  // Always include the task's own title at the end
  if (parts.length === 0 || parts[parts.length - 1] !== task.title) {
    parts.push(task.title);
  }
  return parts.join(' → ');
}

function extractErrorMessage(err: unknown): string {
  const e = err as any;
  return (
    e?.data?.message ||
    e?.data?.error?.message ||
    'Failed to create task'
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

export function CreateTaskModal({
  open,
  onClose,
  defaultParentId,
  defaultOwnerId,
  allTasks,
}: CreateTaskModalProps) {
  // ── RTK Query ───────────────────────────────────────────────────────────
  const [createTask, { isLoading }] = useCreateTaskMutation();

  // ── Form state ──────────────────────────────────────────────────────────
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [ownerId, setOwnerId] = useState(defaultOwnerId ?? '');
  const [status, setStatus] = useState<TaskStatus>('pending');
  const [parentId, setParentId] = useState<string | undefined>(defaultParentId);
  const [source, setSource] = useState('');
  const [metadataRaw, setMetadataRaw] = useState('');

  // ── Validation errors (shown after first submit attempt) ────────────────
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // ── Feedback ────────────────────────────────────────────────────────────
  const [feedback, setFeedback] = useState<FeedbackState>({ kind: 'idle' });

  // ── Markdown panel state ────────────────────────────────────────────────
  const [markdown, setMarkdown] = useState('');
  const [showMarkdownPreview, setShowMarkdownPreview] = useState(true);
  const [markdownManuallyEdited, setMarkdownManuallyEdited] = useState(false);

  // ── Parent autocomplete state ───────────────────────────────────────────
  const [parentSearch, setParentSearch] = useState('');
  const [parentDropdownOpen, setParentDropdownOpen] = useState(false);

  // ── Reset form when modal opens or defaults change ──────────────────────
  useEffect(() => {
    if (open) {
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultParentId, defaultOwnerId]);

  // ── Derived: parent task display ────────────────────────────────────────
  const selectedParentTask = useMemo(
    () => (parentId ? allTasks.find((t) => t._id === parentId) : null),
    [parentId, allTasks],
  );

  const filteredParentTasks = useMemo(() => {
    const query = parentSearch.trim().toLowerCase();
    const list = query
      ? allTasks.filter((t) => t.title.toLowerCase().includes(query))
      : allTasks;
    // Exclude the currently selected parent from the list
    return parentId ? list.filter((t) => t._id !== parentId) : list;
  }, [allTasks, parentSearch, parentId]);

  // ── Markdown generation ─────────────────────────────────────────────────
  const generateMarkdown = useCallback(() => {
    const lines: string[] = [];
    lines.push(`# Task: ${title || 'Untitled'}`);
    lines.push('');
    lines.push('## Assignment');
    lines.push(description || 'No description provided');
    lines.push('');
    lines.push('## Context');
    lines.push(`- **Owner**: ${ownerId || 'Not assigned'}`);
    lines.push(`- **Status**: ${status}`);
    if (source) lines.push(`- **Source**: ${source}`);
    lines.push('');
    lines.push('## Hierarchy');
    if (selectedParentTask) {
      lines.push(`- **Parent**: ${selectedParentTask.title}`);
    } else {
      lines.push('- **Parent**: Root task (no parent)');
    }
    lines.push('');
    if (metadataRaw.trim()) {
      try {
        const parsed = JSON.parse(metadataRaw);
        lines.push('## Metadata');
        lines.push('```json');
        lines.push(JSON.stringify(parsed, null, 2));
        lines.push('```');
      } catch {
        // Skip metadata section if JSON is invalid
      }
    }
    return lines.join('\n');
  }, [title, description, ownerId, status, source, selectedParentTask, metadataRaw]);

  // Auto-regenerate markdown only if user hasn't manually edited it
  useEffect(() => {
    if (!markdownManuallyEdited) {
      setMarkdown(generateMarkdown());
    }
  }, [generateMarkdown, markdownManuallyEdited]);

  const handleRegenerateMarkdown = useCallback(() => {
    setMarkdown(generateMarkdown());
    setMarkdownManuallyEdited(false);
  }, [generateMarkdown]);

  const handleMarkdownChange = useCallback((value: string) => {
    setMarkdown(value);
    setMarkdownManuallyEdited(true);
  }, []);

  // ── Validation ──────────────────────────────────────────────────────────
  const titleError = submitAttempted && !title.trim() ? 'Title is required' : '';
  const ownerError = submitAttempted && !ownerId.trim() ? 'Owner ID is required' : '';

  const metadataError = useMemo(() => {
    if (!submitAttempted || !metadataRaw.trim()) return '';
    try {
      JSON.parse(metadataRaw);
      return '';
    } catch {
      return 'Must be valid JSON';
    }
  }, [submitAttempted, metadataRaw]);

  const isValid =
    title.trim().length > 0 &&
    ownerId.trim().length > 0 &&
    (metadataRaw.trim() === '' || (() => { try { JSON.parse(metadataRaw); return true; } catch { return false; } })());

  // ── Handlers ────────────────────────────────────────────────────────────
  const resetForm = useCallback(() => {
    setTitle('');
    setDescription('');
    setOwnerId(defaultOwnerId ?? '');
    setStatus('pending');
    setParentId(defaultParentId);
    setSource('');
    setMetadataRaw('');
    setMarkdown('');
    setShowMarkdownPreview(true);
    setMarkdownManuallyEdited(false);
    setSubmitAttempted(false);
    setFeedback({ kind: 'idle' });
    setParentSearch('');
    setParentDropdownOpen(false);
  }, [defaultOwnerId, defaultParentId]);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const handleSubmit = useCallback(async () => {
    setSubmitAttempted(true);
    if (!isValid) return;

    let parsedMetadata: Record<string, any> | undefined;
    if (metadataRaw.trim()) {
      try {
        parsedMetadata = JSON.parse(metadataRaw);
      } catch {
        setFeedback({ kind: 'error', message: 'Invalid JSON in metadata' });
        return;
      }
    }

    const dto: CreateTaskDto = {
      ownerId: ownerId.trim(),
      title: title.trim(),
      status,
    };

    if (description.trim()) dto.description = description.trim();
    if (parentId) dto.parentId = parentId;
    if (source.trim()) dto.source = source.trim();
    if (parsedMetadata) dto.metadata = parsedMetadata;
    if (markdown.trim()) dto.markdown = markdown.trim();

    try {
      await createTask(dto).unwrap();
      setFeedback({ kind: 'success' });
      setTimeout(() => {
        handleClose();
      }, 1000);
    } catch (err) {
      setFeedback({ kind: 'error', message: extractErrorMessage(err) });
    }
  }, [
    isValid,
    metadataRaw,
    ownerId,
    title,
    status,
    description,
    parentId,
    source,
    markdown,
    createTask,
    handleClose,
  ]);

  const handleTitleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit],
  );

  const handleSelectParent = useCallback((task: Task) => {
    setParentId(task._id);
    setParentSearch('');
    setParentDropdownOpen(false);
  }, []);

  const handleClearParent = useCallback(() => {
    setParentId(undefined);
    setParentSearch('');
  }, []);

  // ── Render ──────────────────────────────────────────────────────────────
  const isSubtask = !!defaultParentId;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto mx-4 sm:mx-auto">
        <DialogHeader>
          <DialogTitle>{isSubtask ? 'Create Subtask' : 'Create Task'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* ── Title ─────────────────────────────────────────────────── */}
          <div className="space-y-2">
            <Label htmlFor="task-title">
              Title <span className="text-destructive">*</span>
            </Label>
            <Input
              id="task-title"
              placeholder="Task title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={handleTitleKeyDown}
              className={cn(titleError && 'border-destructive focus-visible:ring-destructive')}
              autoFocus
            />
            {titleError && (
              <p className="text-xs text-destructive">{titleError}</p>
            )}
          </div>

          {/* ── Description + Markdown Panel ─────────────────────────── */}
          <div className="space-y-2">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Left: Plain text description */}
              <div className="space-y-2">
                <Label htmlFor="task-description">
                  Description (plain text)
                </Label>
                <textarea
                  id="task-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter task description..."
                  className="w-full min-h-[200px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  rows={8}
                />
              </div>

              {/* Right: Markdown preview/edit */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>AI Assignment Document (Markdown)</Label>
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleRegenerateMarkdown}
                      className="h-7 gap-1 text-xs"
                    >
                      <CheckCircle2 className="h-3 w-3" />
                      Regenerate
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowMarkdownPreview(!showMarkdownPreview)}
                      className="h-7 gap-1 text-xs"
                    >
                      {showMarkdownPreview ? (
                        <>
                          <Pencil className="h-3 w-3" />
                          Edit
                        </>
                      ) : (
                        <>
                          <Eye className="h-3 w-3" />
                          Preview
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {showMarkdownPreview ? (
                  <div
                    className="w-full min-h-[200px] rounded-md border border-input bg-muted/30 px-4 py-3 text-sm prose prose-sm dark:prose-invert max-w-none overflow-y-auto"
                    dangerouslySetInnerHTML={{ __html: renderMarkdown(markdown) }}
                  />
                ) : (
                  <textarea
                    value={markdown}
                    onChange={(e) => handleMarkdownChange(e.target.value)}
                    placeholder="Markdown will be generated automatically..."
                    className="w-full min-h-[200px] rounded-md border border-input bg-background px-3 py-2 text-sm font-mono ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    rows={8}
                  />
                )}
                <p className="text-xs text-muted-foreground">
                  Auto-generated from form data. You can edit manually.
                </p>
              </div>
            </div>
          </div>

          {/* ── Owner ID ──────────────────────────────────────────────── */}
          <div className="space-y-2">
            <Label htmlFor="task-owner">
              Owner ID <span className="text-destructive">*</span>
            </Label>
            <Input
              id="task-owner"
              placeholder="Owner ID"
              value={ownerId}
              onChange={(e) => setOwnerId(e.target.value)}
              className={cn(ownerError && 'border-destructive focus-visible:ring-destructive')}
            />
            {ownerError && (
              <p className="text-xs text-destructive">{ownerError}</p>
            )}
          </div>

          {/* ── Status + Source (side-by-side) ────────────────────────── */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="task-status">Status</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as TaskStatus)}>
                <SelectTrigger id="task-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <span className="flex items-center gap-2">
                        <span
                          className={cn(
                            'inline-block h-2 w-2 rounded-full',
                            opt.dotClass,
                          )}
                        />
                        {opt.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="task-source">Source</Label>
              <Input
                id="task-source"
                placeholder="Source (optional)"
                value={source}
                onChange={(e) => setSource(e.target.value)}
              />
            </div>
          </div>

          {/* ── Parent task (autocomplete) ────────────────────────────── */}
          <div className="space-y-2">
            <Label htmlFor="task-parent">Parent task</Label>
            <div className="relative">
              {selectedParentTask ? (
                <div className="flex items-center gap-2 rounded-md border border-input bg-muted/50 px-3 py-2 text-sm">
                  <span className="flex-1 truncate" title={buildBreadcrumb(selectedParentTask, allTasks)}>
                    {buildBreadcrumb(selectedParentTask, allTasks)}
                  </span>
                  <button
                    type="button"
                    className="shrink-0 rounded-sm p-0.5 text-muted-foreground hover:text-foreground"
                    onClick={handleClearParent}
                    aria-label="Clear parent"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <>
                  <Input
                    id="task-parent"
                    placeholder="Search tasks..."
                    value={parentSearch}
                    onChange={(e) => {
                      setParentSearch(e.target.value);
                      setParentDropdownOpen(true);
                    }}
                    onFocus={() => setParentDropdownOpen(true)}
                    onBlur={() => {
                      // Delay to allow click on dropdown item
                      setTimeout(() => setParentDropdownOpen(false), 150);
                    }}
                  />
                  {parentDropdownOpen && filteredParentTasks.length > 0 && (
                    <div className="absolute z-[100] mt-1 max-h-48 w-full overflow-auto rounded-md border bg-popover text-popover-foreground shadow-md">
                      {filteredParentTasks.map((task) => (
                        <button
                          key={task._id}
                          type="button"
                          className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleSelectParent(task);
                          }}
                        >
                          <span className="truncate font-medium">{task.title}</span>
                          {task.path && (
                            <span className="truncate text-xs text-muted-foreground">
                              {buildBreadcrumb(task, allTasks)}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                  {parentDropdownOpen && parentSearch.trim() && filteredParentTasks.length === 0 && (
                    <div className="absolute z-[100] mt-1 w-full rounded-md border bg-popover px-3 py-2 text-sm text-muted-foreground shadow-md">
                      No tasks found
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* ── Metadata ──────────────────────────────────────────────── */}
          <div className="space-y-2">
            <Label htmlFor="task-metadata">Metadata</Label>
            <textarea
              id="task-metadata"
              placeholder='{"key": "value"}'
              value={metadataRaw}
              onChange={(e) => setMetadataRaw(e.target.value)}
              rows={2}
              className={cn(
                'w-full resize-none rounded-md border border-input bg-background px-3 py-2 font-mono text-sm leading-relaxed placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                metadataError && 'border-destructive focus-visible:ring-destructive',
              )}
              style={{ minHeight: '2.5rem' }}
            />
            {metadataError && (
              <p className="text-xs text-destructive">{metadataError}</p>
            )}
          </div>
        </div>

        {/* ── Feedback badges ─────────────────────────────────────────── */}
        {feedback.kind === 'success' && (
          <div className="flex items-center gap-2 rounded-md bg-green-100 px-3 py-2 text-sm text-green-700 dark:bg-green-900/30 dark:text-green-400">
            <CheckCircle2 className="h-4 w-4" />
            Task created
          </div>
        )}
        {feedback.kind === 'error' && (
          <div className="flex items-center gap-2 rounded-md bg-red-100 px-3 py-2 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">
            <AlertCircle className="h-4 w-4" />
            {feedback.message}
          </div>
        )}

        {/* ── Footer ──────────────────────────────────────────────────── */}
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isValid || isLoading || feedback.kind === 'success'}
          >
            {isLoading ? (
              'Creating...'
            ) : (
              <>
                <Plus className="mr-1.5 h-4 w-4" />
                {isSubtask ? 'Create Subtask' : 'Create Task'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
